import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

/**
 * Full-screen modal that blocks all interaction until a user with
 * must_change_password=true sets a new password. Shown by AdminLayout /
 * StaffLayout when needed.
 */
export default function FirstLoginPasswordChange() {
    const { refresh } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (next.length < 12) return setError("New password must be at least 12 characters.");
        if (!/[a-z]/.test(next)) return setError("Must include a lowercase letter.");
        if (!/[A-Z]/.test(next)) return setError("Must include an uppercase letter.");
        if (!/[0-9]/.test(next)) return setError("Must include a digit.");
        if (!/[^A-Za-z0-9]/.test(next)) return setError("Must include a special character.");
        if (next !== confirm) return setError("Confirmation does not match.");
        if (next === current) return setError("New password must differ from your temporary password.");
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated — welcome to Crossroads to Change.");
            await refresh();
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-[#0E2A47]/85 backdrop-blur-sm grid place-items-center p-6"
            data-testid="first-login-modal"
        >
            <form
                onSubmit={submit}
                className="w-full max-w-md bg-white rounded-2xl border border-[#E5DFD3] p-8 space-y-4 shadow-2xl"
            >
                <div className="text-center">
                    <div className="h-12 w-12 rounded-full bg-[#EFEBE0] grid place-items-center mx-auto mb-3">
                        <KeyRound className="h-5 w-5 text-[#C8A24A]" />
                    </div>
                    <h2 className="font-[Outfit] text-2xl font-semibold tracking-tight">
                        Set a new password
                    </h2>
                    <p className="mt-2 text-[13.5px] text-[#6F6B63]">
                        This is your first login. Please replace the temporary
                        password with one you&apos;ll remember.
                    </p>
                </div>

                <input
                    type="password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    placeholder="Current (temporary) password"
                    className="rtc-input"
                    autoFocus
                    data-testid="first-login-current"
                />
                <input
                    type="password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    placeholder="New password (12+ chars · upper, lower, digit, special)"
                    className="rtc-input"
                    data-testid="first-login-new"
                />
                <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Confirm new password"
                    className="rtc-input"
                    data-testid="first-login-confirm"
                />
                {error && (
                    <p className="text-[13px] text-[#a04545]" data-testid="first-login-error">
                        {error}
                    </p>
                )}
                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-[#0E2A47] text-white py-2.5 text-[14px] font-medium hover:bg-[#08182B] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
                    data-testid="first-login-submit"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save new password
                </button>
            </form>
        </div>
    );
}
